import React, { FC } from 'react'

import PriceRiseBadge from '../price-rise-badge/price-rise-badge'
import SmallPrint from '../small-print'
import { getDurationAndCostThereafter, getMonthlyCost, getSetupCost } from '~/components/deal/utils/costs'
import { usePageContext } from '~/contexts/page-context'
import { Deal } from '~/gql/graphql'
import { useInBucket } from '~/hooks/use-in-bucket'
import { formatSpeedToMbpsNumber } from '~/utils/format-speed'

import DetailBox from './detail-box'
import styles from './styles.module.css'

interface DetailBoxesProps {
  deal: Deal
  openDetailsModal: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  isMobileView: boolean
  cityFibreDealsAvailable: boolean
}

const DetailBoxes: FC<DetailBoxesProps> = ({ deal, openDetailsModal, isMobileView, cityFibreDealsAvailable }) => {
  const showSmallPrint = useInBucket('experiment-broadband-asa-contract-pricing-extended', ['extreme', 'lightweight'])
  const isMidContractPriceRiseTest = useInBucket('experiment-mid-contract-price-rise-details-badge', 'variant')
  const isCityFibreTest = useInBucket('experiment-broadband-city-fibre-trial', 'variant')

  const { slug } = usePageContext()

  const fibreResultsPage = slug === 'fibre_results'

  const isCityFibreTrial = isCityFibreTest && fibreResultsPage && cityFibreDealsAvailable

  const { contractLength, price, services } = deal

  const { downloadSpeed, uploadSpeed } = services?.broadband || {}
  const { channelCount } = services?.tv || {}

  const [realValueCost, decimalCost] = getMonthlyCost(price)
  const discountPeriodAndCost = getDurationAndCostThereafter(price)
  const setupCost = getSetupCost(price)

  const offerSchema = {
    itemProp: 'offers',
    itemScope: true,
    itemType: 'https://schema.org/Offer'
  }

  return (
    <div className={styles.container} data-show-price-details>
      <DetailBox isMobileView={isMobileView}>
        <DetailBox.Row>
          <DetailBox.TitleLarge>{formatSpeedToMbpsNumber(downloadSpeed)}</DetailBox.TitleLarge>
          <DetailBox.Title> Mbps</DetailBox.Title>
        </DetailBox.Row>

        {!isCityFibreTrial && downloadSpeed ? <DetailBox.Row>average UK speed*</DetailBox.Row> : <></>}

        {isCityFibreTrial && uploadSpeed ? (
          <>
            <DetailBox.Row>average download*</DetailBox.Row>

            <DetailBox.Row>
              <DetailBox.TitleMedium>{formatSpeedToMbpsNumber(uploadSpeed)}</DetailBox.TitleMedium>
              <DetailBox.TitleSmall> Mbps</DetailBox.TitleSmall>
            </DetailBox.Row>
            <DetailBox.Row>average upload*</DetailBox.Row>

            <DetailBox.Row>
              <button className={`speed-details ${styles.link}`} onClick={openDetailsModal}>
                Speed details
              </button>
            </DetailBox.Row>
          </>
        ) : (
          <></>
        )}

        {channelCount ? <DetailBox.Footer>{`${channelCount}+ channels`}</DetailBox.Footer> : <></>}
      </DetailBox>

      <DetailBox schema={offerSchema}>
        <DetailBox.Row>
          <DetailBox.TitleLarge>
            <span itemProp='priceCurrency' content='GBP'>
              £
            </span>
            <span itemProp='price' content={`${realValueCost}${decimalCost}`}>
              {realValueCost}
              <small>{decimalCost}</small>
            </span>
          </DetailBox.TitleLarge>
          <DetailBox.Title> a month</DetailBox.Title>
        </DetailBox.Row>

        {discountPeriodAndCost && (
          <DetailBox.Row>
            {discountPeriodAndCost.duration}&nbsp;
            <span className={styles.costThereafter}>{discountPeriodAndCost.costThereafter}</span>
          </DetailBox.Row>
        )}

        <DetailBox.Row> {setupCost}</DetailBox.Row>

        <PriceRiseBadge deal={deal} />

        {showSmallPrint && !isMidContractPriceRiseTest && (
          <DetailBox.Row>
            <SmallPrint contractTerms={deal.provider.contractTerms} />
          </DetailBox.Row>
        )}

        <DetailBox.Row>
          <button className={`price-details ${styles.link}`} onClick={openDetailsModal}>
            Price details
          </button>
        </DetailBox.Row>

        <DetailBox.Footer>{`${contractLength} month contract`}</DetailBox.Footer>
      </DetailBox>
    </div>
  )
}

export default DetailBoxes
